import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

export const RiskEnum = {
    HIGH: "high",
    FAVORABLE: "favorable",
    MODERATE: "moderate",
    LOW: "low",
} as const;
export type RiskEnum = (typeof RiskEnum)[keyof typeof RiskEnum];
const RiskEnumSchema = z.enum(RiskEnum);

export const PestCodeEnum = {
    ARANA_ROJA: "araña_roja",
    ERWINIA_CAROTOVORA: "erwinia_carotovora",
    ALTERNARIA: "alternaria",
    FUSARIUM: "fusarium",
    PHYTOPHTHORA: "phytophthora",
    COTONET: "cotonet",
    CHINCHE_VERDE: "chinche_verde",
    POLILLA_DEL_TOMATE: "polilla_del_tomate",
    PULGON: "pulgon",
    MOSCA_BLANCA: "mosca_blanca",
    THRIPS_PARVISPINUS: "thrips_parvispinus",
    MANCHAS_DE_HUMEDAD: "manchas_de_humedad",
    PUDRICION: "pudricion",
    OIDIO: "oidio",
    MILDIU: "mildiu",
    BOTRITIS: "botritis",
    ABIOTIC_STRESS: "abiotic_stress",
} as const;
export type PestCodeEnum = (typeof PestCodeEnum)[keyof typeof PestCodeEnum];
const PestCodeEnumSchema = z.enum(PestCodeEnum);

const PestSchema = z
    .object({
        id: zIdSchema,
        agentId: z.number(),
        name: z.string().trim(),
        code: PestCodeEnumSchema,
        risk: RiskEnumSchema.nullable(),
        riskPercentage: z.number().nullable(),
        measuredAt: z.string().trim(),
    })
    .brand<"Pest">();
export type PestInputProps = z.input<typeof PestSchema>;
export type PestProps = z.output<typeof PestSchema>;

const PestAvgSchema = z
    .object({
        avgValue: z.number(),
        hourOfDay: z.string().trim(),
        symbol: z.string().trim(),
        name: z.string().trim(),
        type: z.string().trim(),
    })
    .brand<"PestAvg">();
export type PestAvgInputProps = z.input<typeof PestAvgSchema>;
export type PestAvgProps = z.output<typeof PestAvgSchema>;

export class Pest {
    private constructor(private readonly props: Readonly<PestProps>) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    get agentId(): number {
        return this.props.agentId;
    }

    get code(): PestCodeEnum {
        return this.props.code;
    }

    get risk(): Nullable<RiskEnum> {
        return this.props.risk ?? null;
    }

    get riskPercentage(): Nullable<number> {
        return this.props.riskPercentage ?? null;
    }

    get measuredAt(): string {
        return this.props.measuredAt;
    }

    static validate(
        props: Readonly<MaybeValidProps<PestInputProps>>,
    ): Result<PestProps, z.ZodError<PestProps>> {
        const result = PestSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<PestInputProps>>,
    ): Result<PestProps, PartialValid<typeof Pest>> {
        const result = partialSafeParse(PestSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<PestInputProps>,
    ): Result<Pest, z.ZodError<PestProps>> {
        return Pest.validate(props).map(
            (validatedProps) => new Pest(validatedProps),
        );
    }

    static createWithRecovery(
        props: PestInputProps,
    ): Result<Pest, ModelError<typeof Pest>> {
        return Pest.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<PestProps> = {
                    id: error.validProps.id,
                    agentId: 0,
                    name: "",
                    code: PestCodeEnum.OIDIO,
                    risk: null,
                    riskPercentage: null,
                    measuredAt: "",
                };

                const recoveryProps: Unbrand<PestProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Pest>(
                    new Pest(recoveryProps as PestProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Pest(validatedProps));
    }

    toCreateProps(): PestInputProps {
        return {
            id: this.props.id.toCreateProps(),
            name: this.props.name,
            agentId: this.props.agentId,
            code: this.props.code,
            risk: this.props.risk,
            riskPercentage: this.props.riskPercentage,
            measuredAt: this.props.measuredAt,
        };
    }
}
export class PestAvg {
    private constructor(private readonly props: Readonly<PestAvgProps>) {}

    get avgValue(): number {
        return this.props.avgValue;
    }

    get hourOfDay(): string {
        return this.props.hourOfDay;
    }

    get symbol(): string {
        return this.props.symbol;
    }

    get name(): string {
        return this.props.name;
    }

    get type(): string {
        return this.props.type;
    }

    static validate(
        props: Readonly<MaybeValidProps<PestAvgInputProps>>,
    ): Result<PestAvgProps, z.ZodError<PestAvgProps>> {
        const result = PestAvgSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<PestAvgInputProps>>,
    ): Result<PestAvgProps, PartialValid<typeof PestAvg>> {
        const result = partialSafeParse(PestAvgSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<PestAvgInputProps>,
    ): Result<PestAvg, z.ZodError<PestAvgProps>> {
        return PestAvg.validate(props).map(
            (validatedProps) => new PestAvg(validatedProps),
        );
    }

    static createWithRecovery(
        props: PestAvgInputProps,
    ): Result<PestAvg, ModelError<typeof PestAvg>> {
        return PestAvg.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.type)
                    return new NonRecuperableModelError();

                const defaultProps: Unbrand<PestAvgProps> = {
                    avgValue: 0,
                    hourOfDay: "",
                    symbol: "",
                    name: "",
                    type: "",
                };

                const recoveryProps: Unbrand<PestAvgProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof PestAvg>(
                    new PestAvg(recoveryProps as PestAvgProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new PestAvg(validatedProps));
    }

    toCreateProps(): PestAvgInputProps {
        return {
            avgValue: this.props.avgValue,
            hourOfDay: this.props.hourOfDay,
            symbol: this.props.symbol,
            name: this.props.name,
            type: this.props.type,
        };
    }
}

const PestsSchema = z
    .object({
        agents: z.array(
            z.custom<Pest>((val) => val instanceof Pest, {
                error: "Invalid Pest instance",
            }),
        ),
        agentsAvg: z.array(
            z.custom<PestAvg>((val) => val instanceof PestAvg, {
                error: "Invalid PestAvg instance",
            }),
        ),
    })
    .brand<"Pests">();
export type PestsInputProps = z.input<typeof PestsSchema>;
export type PestsProps = z.output<typeof PestsSchema>;

export class Pests {
    private constructor(private readonly props: Readonly<PestsProps>) {}

    get agents(): readonly Readonly<Pest>[] {
        return this.props.agents;
    }

    get agentsAvg(): readonly Readonly<PestAvg>[] {
        return this.props.agentsAvg;
    }

    static empty(): Pests {
        return Pests.create({
            agents: [],
            agentsAvg: [],
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<PestsInputProps>>,
    ): Result<PestsProps, z.ZodError<PestsProps>> {
        const result = PestsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<PestsInputProps>,
    ): Result<Pests, z.ZodError<PestsProps>> {
        return Pests.validate(props).map(
            (validatedProps) => new Pests(validatedProps),
        );
    }
}
